/** @jsxImportSource @emotion/react */
import { useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { css } from "@emotion/react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    TooltipContentProps,
    XAxis,
    YAxis,
} from "recharts";
import {
    ChartTooltip,
    ChartTooltipItem,
    defaultCartesianGridProps,
    defaultLegendProps,
    defaultXAxisProps,
    defaultYAxisProps,
    useBinInterval,
} from "@phoenix/components/chart";
import { useTimeBinScale } from "@phoenix/hooks/useTimeBin";
import { useUTCOffsetMinutes } from "@phoenix/hooks/useUTCOffsetMinutes";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { intShortFormatter } from "@phoenix/utils/numberFormatUtils";
import type { PIIUsageSummaryQuery } from "./__generated__/PIIUsageSummaryQuery.graphql";

const containerCSS = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 8px;
`;

const widgetHeaderCSS = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const chartHeaderCSS = css`
  margin-bottom: 16px;
`;

const bigNumberCSS = css`
  font-size: 32px;
  font-weight: 700;
  color: var(--ac-global-text-color-900);
  margin-right: 8px;
`;

const subtitleCSS = css`
  font-size: 14px;
  color: var(--ac-global-text-color-500);
`;

const chartContainerCSS = css`
  flex: 1;
  min-height: 250px;
`;

function CustomTooltip({
    active,
    payload,
    label,
    valueFormatter,
    scale,
}: TooltipContentProps<number, string> & { valueFormatter: (v: number) => string; scale?: string }) {
    if (active && payload && payload.length) {
        const date = new Date(label || "");
        const isDayScale = scale === "DAY";
        const timeStr = date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(isDayScale ? {} : { hour: '2-digit', minute: '2-digit', hour12: true })
        }).replace(',', '');

        return (
            <ChartTooltip>
                <div style={{ marginBottom: 4, fontWeight: 700, fontSize: 12 }}>{timeStr}</div>
                {payload.map((p, i) => (
                    <ChartTooltipItem
                        key={i}
                        color={p.color || "#ef4444"}
                        shape="circle"
                        name={p.name || ""}
                        value={valueFormatter(p.value as number)}
                    />
                ))}
            </ChartTooltip>
        );
    }
    return null;
}

export function PIIUsageSummary({ projectId, timeRange, fetchKey, widgetId, widgetTitle, onDataExport }: ProjectMetricViewProps) {
    const defaultScale = useTimeBinScale({ timeRange });
    const utcOffsetMinutes = useUTCOffsetMinutes();

    const scale = useMemo(() => {
        if (defaultScale === "MONTH" || defaultScale === "WEEK" || defaultScale === "DAY") return "DAY";
        return defaultScale;
    }, [defaultScale]);

    const data = useLazyLoadQuery<PIIUsageSummaryQuery>(
        graphql`
      query PIIUsageSummaryQuery($projectId: ID!, $timeRange: TimeRange!, $timeBinConfig: TimeBinConfig!, $filterCondition: String) {
        project: node(id: $projectId) {
          ... on Project {
            spanCountTimeSeries(timeRange: $timeRange, timeBinConfig: $timeBinConfig, filterCondition: $filterCondition) {
              data { timestamp okCount errorCount unsetCount }
            }
          }
        }
      }
    `,
        {
            projectId,
            timeRange: { start: timeRange.start?.toISOString(), end: timeRange.end?.toISOString() },
            timeBinConfig: { scale, utcOffsetMinutes },
            filterCondition: 'attributes["evalight_pii_types"] != ""', // Filter for spans where PII types were recorded
        },
        { fetchPolicy: "network-only", fetchKey }
    );

    const { chartData, totalValue } = useMemo(() => {
        const raw = data.project?.spanCountTimeSeries?.data ?? [];
        let total = 0;
        const chart = raw.map((d: any) => {
            const count = (d.okCount || 0) + (d.errorCount || 0) + (d.unsetCount || 0);
            total += count;
            return {
                timestamp: d.timestamp,
                value: count,
                _activity: count
            };
        });

        // Aggressive Auto-Zoom! 🚀
        const firstActiveIdx = chart.findIndex((d: any) => d._activity > 0);
        const lastActiveIdx = chart.length - [...chart].reverse().findIndex((d: any) => d._activity > 0);

        const cropped = (firstActiveIdx === -1) ? chart : chart.slice(
            Math.max(0, firstActiveIdx - 3),
            Math.min(chart.length, lastActiveIdx + 3)
        );

        return { chartData: cropped, totalValue: total };
    }, [data]);

    const timeTickFormatter = (value: string) => {
        const date = new Date(value);
        if (scale === "DAY") {
            return date.toLocaleString('en-US', { month: 'numeric', day: 'numeric' });
        }
        return date.toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', hour12: true });
    };

    const fmt = (v: number) => intShortFormatter(v);

    return (
        <div css={containerCSS}>
            <div css={widgetHeaderCSS}>
                <div css={css`font-size: 14px; font-weight: 600; color: var(--ac-global-text-color-700);`}>PII Redaction Over Time</div>
            </div>

            <div css={chartHeaderCSS}>
                <span css={bigNumberCSS}>{fmt(totalValue)}</span>
                <span css={subtitleCSS}>Detected Redactions</span>
            </div>

            <div css={chartContainerCSS}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 20 }}>
                        <defs>
                            <linearGradient id="colorPII" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
                                <stop offset="95%" stopColor="#ef4444" stopOpacity={0.15} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid {...defaultCartesianGridProps} vertical={false} strokeDasharray="3 3" opacity={0.3} />
                        <XAxis
                            {...defaultXAxisProps}
                            dataKey="timestamp"
                            interval="preserveStartEnd"
                            minTickGap={80}
                            tickFormatter={timeTickFormatter}
                            tick={{ fontSize: 10, fill: "var(--ac-global-text-color-500)" }}
                        />
                        <YAxis
                            {...defaultYAxisProps}
                            width={40}
                            tickFormatter={fmt}
                            tick={{ fontSize: 10 }}
                        />
                        <Tooltip content={(props) => <CustomTooltip {...props} valueFormatter={fmt} scale={scale} />} />
                        <Area
                            type="monotone"
                            dataKey="value"
                            name="Redactions"
                            stroke="#ef4444"
                            strokeWidth={3}
                            fill="url(#colorPII)"
                            dot={{ r: 4, fill: "#facc15", stroke: "#fff", strokeWidth: 2 }}
                            activeDot={{ r: 7, stroke: "#fff", strokeWidth: 2.5 }}
                        />
                        <Legend {...defaultLegendProps} iconType="circle" />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
